/* Pagina Fascicolo Privacy — mostra l'elenco dei PDF generati per un trattamento/docente/esterno
 * con bottoni di download individuali + bottone "Rigenera tutto" + gestione link pubblici (presa visione).
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PrivacyFascicoloPage() {
  const { ambito, codice } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState(null);

  const carica = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`/privacy/fascicolo/${ambito}/${encodeURIComponent(codice)}`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento fascicolo");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { carica(); /* eslint-disable-next-line */ }, [ambito, codice]);

  const genera = async () => {
    // Chiedi la data di stampa (default: oggi in formato gg/mm/aaaa)
    const oggi = new Date().toLocaleDateString("it-IT");
    const ds = window.prompt(
      "Inserisci la data di stampa da iniettare nei documenti (placeholder «Data Stampa»):",
      oggi,
    );
    if (ds === null) return;  // utente ha annullato
    const data_stampa = (ds || "").trim() || oggi;
    if (!window.confirm(`Rigenerare TUTTI i documenti del fascicolo con data di stampa "${data_stampa}"? I PDF precedenti verranno sostituiti.`)) return;
    setGenerating(true); setErr(null);
    try {
      await api.post(`/privacy/fascicolo/${ambito}/${encodeURIComponent(codice)}/genera`, { data_stampa });
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore generazione");
    } finally {
      setGenerating(false);
    }
  };

  const elimina = async () => {
    if (!window.confirm("Eliminare l'intero fascicolo (tutti i PDF generati)?")) return;
    try {
      await api.delete(`/privacy/fascicolo/${ambito}/${encodeURIComponent(codice)}`);
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const ambitoLabel = ambito === "trattamento" ? "Trattamento" : ambito === "docente" ? "Docente" : ambito === "esterno" ? "Esterno" : ambito;
  const ambitoIcon = ambito === "trattamento" ? "📋" : ambito === "docente" ? "👤" : "🏢";
  const ambitoColor = ambito === "trattamento" ? "#1565c0" : ambito === "docente" ? "#6a1b9a" : "#00838f";

  const documenti = data?.documenti || [];
  const generatiCount = documenti.filter(d => d.gridfs_id).length;
  const erroriCount = documenti.filter(d => d.errore).length;

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
        <button onClick={() => navigate(-1)} data-testid="fasc-back"
          style={{ padding: "6px 12px", background: "white", border: "1px solid #999", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          ← Indietro
        </button>
        <h2 style={{ color: "#37474f", fontSize: 18, margin: 0 }}>
          {ambitoIcon} Fascicolo {ambitoLabel}
          <span style={{ color: ambitoColor, marginLeft: 8 }}>{data?.label || codice}</span>
        </h2>
        <span style={{ marginLeft: 14, fontSize: 12, color: "#666" }}>
          {documenti.length === 0 ? "Nessun modello configurato per questo ambito"
            : `${generatiCount}/${documenti.length} generati`}
          {erroriCount > 0 && <span style={{ color: "#c62828", fontWeight: 700, marginLeft: 6 }}>· {erroriCount} errori</span>}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {generatiCount > 0 && (
            <>
              <a href={`${API_URL}/api/privacy/fascicolo/${ambito}/${encodeURIComponent(codice)}/download-merged?inline=1`}
                target="_blank" rel="noopener noreferrer" data-testid="fasc-merged-view"
                title="Apri in anteprima un UNICO PDF con tutti i documenti del fascicolo"
                style={{ padding: "8px 14px", background: "#4527a0", color: "white", textDecoration: "none", fontSize: 12, fontWeight: 700 }}>
                👁 PDF unico
              </a>
              <a href={`${API_URL}/api/privacy/fascicolo/${ambito}/${encodeURIComponent(codice)}/download-merged`}
                data-testid="fasc-merged-download"
                title="Scarica un UNICO PDF con tutti i documenti del fascicolo uniti in sequenza"
                style={{ padding: "8px 14px", background: "#283593", color: "white", textDecoration: "none", fontSize: 12, fontWeight: 700 }}>
                📑 Scarica PDF unico
              </a>
            </>
          )}
          {data?.generato_il && (
            <button onClick={elimina} data-testid="fasc-delete"
              style={{ padding: "8px 14px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              🗑 Elimina fascicolo
            </button>
          )}
          <button onClick={genera} disabled={generating || documenti.length === 0} data-testid="fasc-genera"
            style={{ padding: "8px 18px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: generating ? "not-allowed" : "pointer", opacity: generating ? 0.6 : 1 }}>
            {generating ? "⏳ Generazione in corso..." : data?.generato_il ? "🔄 Rigenera tutto" : "⚡ Genera fascicolo"}
          </button>
        </div>
      </div>

      {data?.generato_il && (
        <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>
          Ultima generazione: <b>{new Date(data.generato_il).toLocaleString("it-IT")}</b>
          {data.generato_da && <> · da <b>{data.generato_da}</b></>}
        </div>
      )}

      {err && (
        <div style={{ padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, marginBottom: 12, border: "1px solid #ef9a9a" }}>
          {err}
          <button onClick={() => setErr(null)} style={{ float: "right", background: "none", border: "none", cursor: "pointer", color: "#c00", fontWeight: 700 }}>✕</button>
        </div>
      )}

      {loading && <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento...</div>}

      {!loading && documenti.length === 0 && (
        <div style={{ padding: 30, background: "#fff8e1", border: "1px solid #ffe082", textAlign: "center" }}>
          <div style={{ fontSize: 14, color: "#6d4c00", marginBottom: 8, fontWeight: 700 }}>
            Nessun modello configurato per ambito "{ambitoLabel}".
          </div>
          <div style={{ fontSize: 12, color: "#6d4c00" }}>
            Vai su <b>Configurazione Fascicolo</b> e aggiungi i modelli da includere.
          </div>
        </div>
      )}

      {!loading && documenti.length > 0 && (
        <div style={{ background: "white", border: "1px solid #ccc" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead className="thead-blue">
              <tr>
                <th style={{ width: 36, padding: 6, textAlign: "center" }}>#</th>
                <th style={{ width: 90, padding: 6, textAlign: "left" }}>CODICE</th>
                <th style={{ padding: 6, textAlign: "left" }}>NOME MODELLO</th>
                <th style={{ width: 130, padding: 6 }}>STATO</th>
                <th style={{ width: 90, padding: 6, textAlign: "right" }}>DIMENSIONE</th>
                <th style={{ width: 220, padding: 6, textAlign: "center" }}>AZIONI</th>
              </tr>
            </thead>
            <tbody>
              {documenti.map((d, i) => {
                const ok = !!d.gridfs_id;
                const hasErr = !!d.errore;
                const notApplicable = d.applicabile === false;
                return (
                  <tr key={d.modello_codice} style={{ borderTop: "1px solid #eee", opacity: notApplicable ? 0.55 : 1, background: notApplicable ? "#fafafa" : "transparent" }} data-testid={`fasc-row-${i}`}>
                    <td style={{ textAlign: "center", color: "#888", padding: 6 }}>{i + 1}</td>
                    <td style={{ fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1", padding: 6 }}>{d.modello_codice}</td>
                    <td style={{ padding: 6 }}>
                      <div style={{ fontWeight: 600 }}>{d.modello_nome}</div>
                      {hasErr && <div style={{ color: "#c62828", fontSize: 10, marginTop: 2 }}>⚠ {d.errore}</div>}
                      {notApplicable && (
                        <div style={{ color: "#e65100", fontSize: 10, marginTop: 2, fontStyle: "italic" }} data-testid={`fasc-not-applicable-${i}`}>
                          ⊘ Non applicabile: documento opzionale, il docente non ha la nomina di tipo «{d.tipo_nomina_codice}»
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: "center", padding: 6 }}>
                      {notApplicable && <span style={{ background: "#fff3e0", color: "#e65100", padding: "2px 8px", fontSize: 10, fontWeight: 700, borderRadius: 10 }}>⊘ Saltato</span>}
                      {!notApplicable && ok && <span style={{ background: "#e8f5e9", color: "#1b5e20", padding: "2px 8px", fontSize: 10, fontWeight: 700, borderRadius: 10 }}>✓ Generato</span>}
                      {!notApplicable && hasErr && <span style={{ background: "#ffebee", color: "#c62828", padding: "2px 8px", fontSize: 10, fontWeight: 700, borderRadius: 10 }}>⚠ Errore</span>}
                      {!notApplicable && !ok && !hasErr && <span style={{ background: "#f5f5f5", color: "#888", padding: "2px 8px", fontSize: 10, fontWeight: 700, borderRadius: 10 }}>— Non generato</span>}
                    </td>
                    <td style={{ textAlign: "right", padding: 6, color: "#666", fontSize: 11 }}>
                      {d.size ? `${Math.round(d.size / 1024)} KB` : "—"}
                    </td>
                    <td style={{ textAlign: "center", padding: 6, whiteSpace: "nowrap" }}>
                      <a href={`${API_URL}/api/privacy/fascicolo/${ambito}/${encodeURIComponent(codice)}/download/${encodeURIComponent(d.modello_codice)}?inline=1`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`fasc-view-${i}`}
                        style={{ display: ok ? "inline-block" : "none", background: "#6a1b9a", color: "white", textDecoration: "none", padding: "4px 10px", fontSize: 11, fontWeight: 700, marginRight: 4 }}>
                        👁 Anteprima
                      </a>
                      <a href={`${API_URL}/api/privacy/fascicolo/${ambito}/${encodeURIComponent(codice)}/download/${encodeURIComponent(d.modello_codice)}`}
                        data-testid={`fasc-download-${i}`}
                        style={{ display: ok ? "inline-block" : "none", background: "#1565c0", color: "white", textDecoration: "none", padding: "4px 10px", fontSize: 11, fontWeight: 700 }}>
                        📄 Scarica
                      </a>
                      {!ok && !notApplicable && <span style={{ color: "#aaa", fontSize: 11 }}>— Clicca "Genera fascicolo"</span>}
                      {!ok && notApplicable && <span style={{ color: "#aaa", fontSize: 11 }}>— non generato (saltato)</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sezione Link Pubblici v2 con Presa Visione — disponibile per tutti gli ambiti */}
      {generatiCount > 0 && (
        <PublicLinksPanel ambito={ambito} codice={codice} documenti={documenti} />
      )}
    </div>
  );
}

/* ============== Pannello Link Pubblici (v2) ============== */

function PublicLinksPanel({ ambito, codice, documenti }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [giorniValidi, setGiorniValidi] = useState("");  // vuoto = illimitato
  const [expanded, setExpanded] = useState({});  // {token: bool}
  const [createdLink, setCreatedLink] = useState(null);
  const [err, setErr] = useState(null);

  const carica = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/privacy/fascicolo/links`, { params: { ambito, codice } });
      setLinks(r.data?.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento link");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { carica(); /* eslint-disable-next-line */ }, [ambito, codice]);

  const creaLink = async () => {
    setCreating(true); setErr(null); setCreatedLink(null);
    try {
      const body = { ambito, codice_entita: codice };
      if (giorniValidi && Number(giorniValidi) > 0) body.giorni_validi = Number(giorniValidi);
      const r = await api.post(`/privacy/fascicolo/link`, body);
      const fullUrl = `${window.location.origin}/fascicolo/${r.data.token}`;
      setCreatedLink({ ...r.data, url: fullUrl });
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore creazione link");
    } finally {
      setCreating(false);
    }
  };

  const revocaLink = async (token) => {
    if (!window.confirm("Revocare questo link? Il destinatario non potrà più accedervi.")) return;
    try {
      await api.post(`/privacy/fascicolo/link/${encodeURIComponent(token)}/revoke`);
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore revoca");
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/fascicolo/${token}`;
    try {
      navigator.clipboard.writeText(url);
      alert(`Link copiato negli appunti:\n${url}`);
    } catch {
      window.prompt("Copia il link:", url);
    }
  };

  const inviaMail = async (token) => {
    const overrideEmail = window.prompt(
      "Email destinatario (lascia vuoto per usare quella dell'anagrafica):",
      ""
    );
    if (overrideEmail === null) return; // annullato
    try {
      const body = {};
      if (overrideEmail.trim()) body.email = overrideEmail.trim();
      const r = await api.post(`/privacy/fascicolo/link/${encodeURIComponent(token)}/send-email`, body);
      alert(`✓ Email inviata a ${r.data?.sent_to || "destinatario"}`);
      await carica();
    } catch (e) {
      alert("⚠ Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT") : "—";
  const stato = (l) => {
    if (l.revoked) return { label: "REVOCATO", color: "#888" };
    if (l.expires_at && new Date(l.expires_at) < new Date()) return { label: "SCADUTO", color: "#c62828" };
    return { label: "ATTIVO", color: "#2e7d32" };
  };

  return (
    <div style={{ marginTop: 20, background: "white", border: "1px solid #ccc" }}>
      <div style={{ background: "#ef6c00", color: "white", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>🔗 Link pubblici (Presa Visione)</div>
        <div style={{ fontSize: 11, opacity: 0.9 }}>
          Genera link da inviare al destinatario per visualizzare i documenti e confermare la presa visione.
        </div>
        <button onClick={() => { setShowCreate(true); setCreatedLink(null); setGiorniValidi(""); }}
          data-testid="fasc-link-create-btn"
          style={{ marginLeft: "auto", padding: "6px 14px", background: "white", color: "#ef6c00", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          + Nuovo link
        </button>
      </div>

      {err && (
        <div style={{ padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, borderBottom: "1px solid #ef9a9a" }}>
          {err}
          <button onClick={() => setErr(null)} style={{ float: "right", background: "none", border: "none", cursor: "pointer", color: "#c00", fontWeight: 700 }}>✕</button>
        </div>
      )}

      {/* Modal creazione link */}
      {showCreate && (
        <div style={{ padding: 16, background: "#fff8e1", borderBottom: "1px solid #ffe082" }}>
          {!createdLink ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#6d4c00", marginBottom: 10 }}>
                Crea un nuovo link pubblico per <b>{ambito} {codice}</b>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ fontSize: 12, color: "#555", display: "flex", flexDirection: "column", gap: 4 }}>
                  <span>Validità (giorni)</span>
                  <input type="number" min="1" max="3650" placeholder="vuoto = illimitato"
                    value={giorniValidi} onChange={(e) => setGiorniValidi(e.target.value)}
                    data-testid="fasc-link-days"
                    style={{ padding: "6px 10px", border: "1px solid #aaa", fontSize: 13, width: 200 }} />
                </label>
                <div style={{ fontSize: 11, color: "#888", flex: 1 }}>
                  Lascia vuoto per un link <b>senza scadenza</b>. Potrai sempre revocarlo manualmente.
                </div>
                <button onClick={creaLink} disabled={creating}
                  data-testid="fasc-link-confirm"
                  style={{ padding: "8px 18px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: creating ? "not-allowed" : "pointer", opacity: creating ? 0.6 : 1 }}>
                  {creating ? "⏳ Creazione..." : "✅ Genera link"}
                </button>
                <button onClick={() => setShowCreate(false)}
                  style={{ padding: "8px 14px", background: "white", border: "1px solid #999", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  Annulla
                </button>
              </div>
            </>
          ) : (
            <div data-testid="fasc-link-created">
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1b5e20", marginBottom: 8 }}>
                ✅ Link creato con successo. Copialo e invialo al destinatario:
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input readOnly value={createdLink.url}
                  onFocus={(e) => e.target.select()}
                  style={{ flex: 1, minWidth: 380, padding: "8px 10px", border: "1px solid #aaa", fontSize: 12, fontFamily: "Consolas, monospace", background: "white" }} />
                <button onClick={() => { navigator.clipboard?.writeText(createdLink.url); alert("Link copiato."); }}
                  style={{ padding: "8px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  📋 Copia
                </button>
                <a href={createdLink.url} target="_blank" rel="noopener noreferrer"
                  style={{ padding: "8px 14px", background: "#ef6c00", color: "white", textDecoration: "none", fontSize: 12, fontWeight: 700 }}>
                  👁 Apri
                </a>
                <button onClick={() => setShowCreate(false)}
                  style={{ padding: "8px 14px", background: "white", border: "1px solid #999", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  Chiudi
                </button>
              </div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>
                Scadenza: <b>{createdLink.expires_at ? new Date(createdLink.expires_at).toLocaleDateString("it-IT") : "nessuna (illimitato)"}</b>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabella link esistenti */}
      {loading && <div style={{ padding: 14, textAlign: "center", color: "#888", fontSize: 12 }}>Caricamento link...</div>}
      {!loading && links.length === 0 && (
        <div style={{ padding: 18, textAlign: "center", color: "#888", fontSize: 12 }}>
          Nessun link generato. Clicca "+ Nuovo link" per crearne uno.
        </div>
      )}
      {!loading && links.length > 0 && (
        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
          <thead style={{ background: "#fff3e0", color: "#bf360c" }}>
            <tr>
              <th style={{ padding: 6, textAlign: "left", width: 130 }}>CREATO IL</th>
              <th style={{ padding: 6, textAlign: "left", width: 110 }}>DA</th>
              <th style={{ padding: 6, textAlign: "center", width: 100 }}>SCADENZA</th>
              <th style={{ padding: 6, textAlign: "center", width: 80 }}>STATO</th>
              <th style={{ padding: 6, textAlign: "center", width: 70 }}>VISTE</th>
              <th style={{ padding: 6, textAlign: "center", width: 110 }}>PRESA VISIONE</th>
              <th style={{ padding: 6, textAlign: "center", width: 240 }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {links.map((l, i) => {
              const s = stato(l);
              const pv = l.presa_visione || {};
              const pvCount = Object.keys(pv).length;
              const isExp = !!expanded[l.token];
              return (
                <>
                  <tr key={l.token} style={{ borderTop: "1px solid #eee" }} data-testid={`fasc-link-row-${i}`}>
                    <td style={{ padding: 6 }}>{fmtDate(l.created_at)}</td>
                    <td style={{ padding: 6, color: "#666" }}>{l.created_by || "—"}</td>
                    <td style={{ padding: 6, textAlign: "center" }}>
                      {l.expires_at ? new Date(l.expires_at).toLocaleDateString("it-IT") :
                        <span style={{ color: "#888", fontStyle: "italic" }}>illimitato</span>}
                    </td>
                    <td style={{ padding: 6, textAlign: "center" }}>
                      <span style={{ background: s.color, color: "white", padding: "1px 8px", fontSize: 10, fontWeight: 700, borderRadius: 10 }}>
                        {s.label}
                      </span>
                    </td>
                    <td style={{ padding: 6, textAlign: "center", fontWeight: 700 }}>
                      {l.view_count || 0}
                      {l.first_viewed_at && (
                        <div style={{ fontSize: 9, color: "#888", fontWeight: 400 }}>
                          {fmtDate(l.last_viewed_at || l.first_viewed_at)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 6, textAlign: "center" }}>
                      <span style={{ fontWeight: 700, color: pvCount === documenti.length && pvCount > 0 ? "#1b5e20" : pvCount > 0 ? "#ef6c00" : "#888" }}>
                        {pvCount}/{documenti.length}
                      </span>
                      {pvCount > 0 && (
                        <button onClick={() => setExpanded(e => ({ ...e, [l.token]: !isExp }))}
                          data-testid={`fasc-link-expand-${i}`}
                          style={{ marginLeft: 4, background: "none", border: "none", cursor: "pointer", color: "#1565c0", fontSize: 11 }}>
                          {isExp ? "▼" : "▶"}
                        </button>
                      )}
                    </td>
                    <td style={{ padding: 6, textAlign: "center", whiteSpace: "nowrap" }}>
                      {!l.revoked && (
                        <button onClick={() => inviaMail(l.token)} title="Invia mail al destinatario con il link"
                          data-testid={`fasc-link-mail-${i}`}
                          style={{ background: "#2e7d32", color: "white", border: "none", padding: "3px 9px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3 }}>
                          📧 Mail
                        </button>
                      )}
                      <button onClick={() => copyLink(l.token)} title="Copia link"
                        data-testid={`fasc-link-copy-${i}`}
                        style={{ background: "#1565c0", color: "white", border: "none", padding: "3px 9px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3 }}>
                        📋 Copia
                      </button>
                      <a href={`/fascicolo/${l.token}`} target="_blank" rel="noopener noreferrer"
                        style={{ display: "inline-block", background: "#ef6c00", color: "white", textDecoration: "none", padding: "3px 9px", fontSize: 10, fontWeight: 700, marginRight: 3 }}>
                        👁 Apri
                      </a>
                      {!l.revoked && (
                        <button onClick={() => revocaLink(l.token)} title="Revoca link"
                          data-testid={`fasc-link-revoke-${i}`}
                          style={{ background: "#c62828", color: "white", border: "none", padding: "3px 9px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
                          ✕ Revoca
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExp && pvCount > 0 && (
                    <tr style={{ background: "#fafafa" }}>
                      <td colSpan={7} style={{ padding: "8px 14px" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#37474f", marginBottom: 4 }}>Dettaglio presa visione:</div>
                        <table style={{ width: "100%", fontSize: 10 }}>
                          <thead>
                            <tr style={{ color: "#666" }}>
                              <th style={{ textAlign: "left", padding: 3 }}>MODELLO</th>
                              <th style={{ textAlign: "left", padding: 3 }}>QUANDO</th>
                              <th style={{ textAlign: "left", padding: 3 }}>IP</th>
                              <th style={{ textAlign: "left", padding: 3 }}>USER-AGENT</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(pv).map(([mc, info]) => {
                              const docMeta = documenti.find(d => d.modello_codice === mc);
                              return (
                                <tr key={mc} style={{ borderTop: "1px solid #eee" }}>
                                  <td style={{ padding: 3 }}>
                                    <b>{mc}</b> {docMeta?.modello_nome && <span style={{ color: "#666" }}>· {docMeta.modello_nome}</span>}
                                  </td>
                                  <td style={{ padding: 3 }}>{fmtDate(info.at)}</td>
                                  <td style={{ padding: 3, fontFamily: "Consolas, monospace" }}>{info.ip || "—"}</td>
                                  <td style={{ padding: 3, color: "#888", fontSize: 9, maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{info.user_agent || "—"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
